// "Push to Constant Contact" button for the Email editor's output
// toolbar (P-048 / D-143). Closes V2.2.
//
// **Extracted to its own file** because the state machine + dispatch
// surface (4 connection states × disconnect/push/reauth flows) would
// push `email-editor-output.tsx` past the 200-line guideline. Single
// consumer — `<OutputToolbar />` mounts one instance per Email.
//
// **State machine:**
//   loading       — initial mount; cc-oauth chunk loading + isConnected check
//   disconnected  — no CC tokens; button reads "Connect to Constant Contact"
//   connected     — tokens present; button reads "Push to Constant Contact"
//                   (disabled with tooltip when subjectLine is empty)
//   pushing       — POST in-flight; button reads "Pushing…" with aria-busy
//
// **Q4 placement (Banks-confirmed at session opening per [[feedback_questions_one_at_a_time]]):**
// Add alongside Copy HTML / Download HTML / View in Tab — preserves the
// V1 manual workflow as a fallback if CC API ever fails or staff prefers
// the paste-in-CC path.
//
// **Dynamic imports for cc-oauth + cc-api** mirror the SettingsScreen
// pattern (D-141 + D-142). Both modules live in a lazy chunk so the
// editor's initial bundle stays free of CC-integration code until staff
// actually engages the Push flow.
//
// **`CCReAuthRequired` branch.** The error class is dynamic-imported
// alongside `createCampaign` so the `instanceof` check works without
// statically pinning cc-oauth into the editor chunk. Surfaces a
// dedicated "Reconnect" toast action (vs the "Try again" action on a
// generic push error) — clicking it re-fires `startOAuth()` for the
// same UX as the Settings page's Connect button.
//
// **Toast UX.** Success: short message + "Open draft in CC →" action
// that `window.open`s the campaign edit URL with `noopener,noreferrer`.
// Error: sanitized CC message (already truncated + control-char-stripped
// in `cc-api.readSanitizedError`) + a "Try again" retry affordance.
// Reauth: "Reconnect" affordance + immediate state flip to
// 'disconnected' so the button itself becomes the Connect entry point.
//
// **Subject-line gating.** When `state === 'connected'` AND
// `email.subjectLine === ''`, the button is `disabled={true}` with a
// `title=` tooltip prompting staff to add a subject. The Push API call
// would reject the empty subject anyway (CC requires a non-empty
// subject); the UI gate prevents the wasted round-trip + surfaces the
// requirement plainly rather than via a CC 4xx error toast.

import { useEffect, useState } from 'preact/hooks'

import { errorMessage } from '../../lib/error'
import type { Email } from '../../store/schemas'
import { secondaryButtonClass } from '../button-classes'
import { useToast } from '../toast/use-toast'

type ConnectionState = 'loading' | 'disconnected' | 'connected' | 'pushing'

interface PushButtonProps {
  readonly email: Email
  readonly tabIndex: number
  readonly onFocus: () => void
}

export function PushButton({ email, tabIndex, onFocus }: PushButtonProps) {
  const { addToast } = useToast()
  const [state, setState] = useState<ConnectionState>('loading')

  // Initial connection check on mount. Lazy-loads cc-oauth chunk; a
  // failed dynamic import falls back to 'disconnected' so the Connect
  // affordance still renders + retries the chunk load when clicked.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const { isConnected } = await import('../../lib/cc-oauth')
        if (cancelled) return
        setState(isConnected() ? 'connected' : 'disconnected')
      } catch {
        if (cancelled) return
        setState('disconnected')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const handleConnect = async () => {
    try {
      const { startOAuth } = await import('../../lib/cc-oauth')
      await startOAuth()
      // startOAuth navigates the top-level window to CC's authorize URL
      // — control doesn't return here in production. The success path
      // resumes after CC redirects back to /auth/cc-callback.
    } catch (err) {
      addToast({
        kind: 'error',
        message: errorMessage(err, "Couldn't connect to Constant Contact."),
      })
    }
  }

  const handlePush = async () => {
    setState('pushing')
    let createCampaign: typeof import('../../lib/cc-api').createCampaign
    let CCReAuthRequired: typeof import('../../lib/cc-oauth').CCReAuthRequired
    try {
      ;({ createCampaign } = await import('../../lib/cc-api'))
      ;({ CCReAuthRequired } = await import('../../lib/cc-oauth'))
    } catch (err) {
      console.error('PushButton.handlePush: chunk load failed', err)
      addToast({
        kind: 'error',
        message: "Couldn't load the Constant Contact push module. Please retry.",
      })
      setState('connected')
      return
    }

    try {
      const result = await createCampaign(email)
      addToast({
        kind: 'success',
        message: '✓ Pushed to Constant Contact',
        action: {
          label: 'Open draft in CC →',
          onClick: () => {
            window.open(result.campaignUrl, '_blank', 'noopener,noreferrer')
          },
        },
      })
      setState('connected')
    } catch (err) {
      if (err instanceof CCReAuthRequired) {
        addToast({
          kind: 'error',
          message: 'Reconnect to Constant Contact to push this email.',
          action: {
            label: 'Reconnect',
            onClick: () => {
              void handleConnect()
            },
          },
        })
        setState('disconnected')
        return
      }
      addToast({
        kind: 'error',
        message: errorMessage(err, "Couldn't push to Constant Contact."),
        action: {
          label: 'Try again',
          onClick: () => {
            void handlePush()
          },
        },
      })
      setState('connected')
    }
  }

  // Shared button props — tabIndex + onFocus come from the toolbar's
  // roving-tab-index walk. type="button" prevents implicit form submit.
  const sharedProps = {
    type: 'button' as const,
    tabIndex,
    onFocus,
    class: secondaryButtonClass,
  }

  if (state === 'loading') {
    return (
      <button {...sharedProps} disabled aria-busy="true">
        Push to Constant Contact
      </button>
    )
  }

  if (state === 'disconnected') {
    return (
      <button {...sharedProps} onClick={() => void handleConnect()}>
        Connect to Constant Contact
      </button>
    )
  }

  if (state === 'pushing') {
    return (
      <button {...sharedProps} disabled aria-busy="true">
        Pushing…
      </button>
    )
  }

  // state === 'connected'
  const subjectEmpty = email.subjectLine === ''
  return (
    <button
      {...sharedProps}
      onClick={() => void handlePush()}
      disabled={subjectEmpty}
      title={subjectEmpty ? 'Add a subject line before pushing' : undefined}
    >
      Push to Constant Contact
    </button>
  )
}
